<script lang="ts" setup>
	import { useSeoSchema } from '~/utils/seo-schema'
	import type { H1Config } from '~/components/ui/h1.vue'
	import type { FAQContext } from '~/components/main/faq.vue'
	import type { usedHospitalContext } from '~/components/main/most-used-hospitals.vue'
	import type { teamBenefitsContext } from '~/components/main/productivity-benefits.vue'
	import type { GuideListContext } from '~/components/molecules/important-guides.vue'
	import type { ProcessesStepContext } from '~/components/molecules/process-steps.vue'

	definePageMeta({
		layout: 'default'
	})
	const seo = {
		title: 'MyCoverGenius | ZenCare | Quality HMO for businesses',
		description: 'Premium health insurance designed for businesses with bigger teams',
		tags: ['Health insurance', 'HMO', 'ZenCare', 'Healthcare', 'Medical insurance'],
		url: `${import.meta.env.VITE_BUSINESS_URL}/zencare`
	}
	useSeoMeta({
		title: seo.title,
		description: seo.description,
		ogTitle: seo.title,
		ogDescription: seo.description,
		ogType: 'website',
		ogLocale: 'en_NG',
		articleTag: seo.tags,
		ogUrl: seo.url,
		twitterTitle: seo.title,
		twitterDescription: seo.description,
		twitterSite: '@mycovergenius',
		twitterCard: 'summary_large_image',
		twitterAppUrlGoogleplay: seo.url,
		twitterAppUrlIphone: seo.url,
		robots: {
			all: 'true',
			follow: 'true',
			index: 'true'
		}
	})
	const isDialogOpen = shallowRef(false)
	const showBenefits = shallowRef(false)
	const focusMode = shallowRef(false)
	const { navigateToProduct, viewMoreProducts } = useProductsStore()
	const faqList = ref<FAQContext[]>([
		{
			question: 'When will my policy become active after I activate it?',
			answer: 'Your H.M.O policy will become active 24 hours after activation. You can start accessing care once the activation period is complete.',
			open: true
		},
		{
			question: 'How do I access the list of hospitals on ZenCare?',
			answer: "You can see the hospital list directly on the ZenCare page or access it from your dashboard once you're logged in. This will provide you with a comprehensive list of all the hospitals where you can receive care under your plan.",
			open: false
		},
		{
			question: 'What is the least number of staff to add on ZenCare?',
			answer: 'As a business, the minimum number of staff you can add on the ZenCare plan is 2.',
			open: false
		},
		{
			question: 'Can I remove a staff from my plan when he/she leaves the company?',
			answer: 'Yes, you can easily remove staff members who no longer work at your company, either manually or in batches. Your dashboard enables this to help simplify plan and employee management.',
			open: false
		},
		{
			question: 'Is there a penalty for late renewal?',
			answer: 'If you renew within <span class="font-semibold">7 days after expiry</span>, your plan will reactivate within 24 hours. If you renew after <span class="font-semibold">7 days of expiry</span>, your plan will have a <span class="font-semibold">7-day waiting period</span> before it becomes active.',
			open: false
		}
	])

	const { generateProductSchema, generateFaqSchema, generateBreadcrumbSchema } = useSeoSchema()

	useHead({
		script: [
			{
				type: 'application/ld+json',
				innerHTML: JSON.stringify(
					generateProductSchema({
						name: 'ZenCare',
						description: seo.description,
						image: 'https://res.cloudinary.com/dzhhy3ovo/image/upload/v1727082910/zencare-lead_bsllzz.webp',
						url: seo.url,
						price: '0'
					})
				)
			},
			{ type: 'application/ld+json', innerHTML: JSON.stringify(generateFaqSchema(faqList.value)) },
			{
				type: 'application/ld+json',
				innerHTML: JSON.stringify(
					generateBreadcrumbSchema([
						{ name: 'Home', item: 'https://mycovergenius.com/' },
						{ name: 'ZenCare', item: seo.url }
					])
				)
			}
		],
		link: [{ rel: 'canonical', href: seo.url }]
	})

	const onboardingProcesses = shallowRef<ProcessesStepContext>([
		{
			icon: {
				dir: 'random',
				icon: 'shopping-cart',
				height: '80',
				width: '78'
			},
			title: 'Choose your plan',
			description: 'Select any ZenCare plan that best fits your <br /> business needs'
		},
		{
			icon: {
				dir: 'random',
				icon: 'wishlist-blue',
				width: '60',
				height: '80'
			},
			title: 'Onboard your staff',
			description: 'Onboard your staff through our simple <br /> batch operations'
		},
		{
			icon: {
				dir: 'random',
				icon: 'umbrella-2',
				height: '80',
				width: '80'
			},
			title: 'Access healthcare instantly',
			description:
				'Staff can now access care at their preferred <br /> hospital with their digital HMO ID. It’s that simple'
		}
	])

	const mostlyUsedHospitals: usedHospitalContext[] = [
		{
			name: {
				text: 'Cedarcare'
			},
			location: {
				text: 'Ogombo road, Off Abraham Adesanya Round about, Ajah, Lagos'
			}
		},
		{
			name: {
				text: "Lifeline Children's Hospital"
			},
			location: {
				text: '16B College rd. Ibadan, Oyo'
			}
		},
		{
			name: {
				text: 'Utmost Care Hospital'
			},
			location: {
				text: '5, Golf Road, Lakowe, Ibeju-Lekki, Lagos'
			}
		},
		{
			name: {
				text: 'Blue cross hospital'
			},
			location: {
				text: '48 Ijaiye Rd, Ogba, Ikeja, Lagos'
			}
		},
		{
			name: {
				text: 'Lennox Hospital'
			},
			location: {
				text: 'Olokonla, Plot 7 Floodgate St, Ajah, Lagos'
			}
		},
		{
			name: {
				text: 'Grover Medical & Diagnostics'
			},
			location: {
				text: '81A Younis Bashorun Street, Off Ajose Adeogun St, Lagos'
			}
		},
		{
			name: {
				text: 'DFO Medical Clinic'
			},
			location: {
				text: '1 Banji Adewole Lane Tabade Pharmacy building, Pako Busstop, Akoka RD, Yaba'
			}
		},
		{
			name: {
				text: 'County Hospital'
			},
			location: {
				text: '43/45 Isheri Rd, Aguda, Ogba, Lagos'
			}
		},
		{
			name: {
				text: 'Marie Stopes Medical Centre'
			},
			location: {
				text: '13 Aideyan Street, Off Ihama Road, Benin-City, Edo State'
			}
		},
		{
			name: {
				text: 'R-Jolad Hospital'
			},
			location: {
				text: '6, Olabode Street Off MM International Airport Road, Ajao Estate, Isolo, Lagos'
			}
		}
	]

	const btnClass: H1Config = {
		wrapperClass: {
			alignItems: '',
			overflow: ''
		},
		hClass: {
			wrapper: {
				alignItems: 'items-start'
			}
		},
		paragraphClass: {
			maxWidth: 'max-w-[634px] md:leading-[32px]',
			text: 'xl:text-xl'
		},
		btn: {
			base: 'md:mt-20',
			space: '',
			iconWrapper: {
				base: ' flex-col inline-flex'
			},
			primary: {
				btn: {
					base: 'bg-white  text-base text-health-800 hover:text-white',
					onHoverPseudo: 'hover:before:bg-health-500',
					width: ''
				},
				text: {
					size: ''
				}
			},
			secondary: {
				base: 'default-btn text-center justify-center gap-x-3'
			}
		}
	}

	const teamBenefits: teamBenefitsContext = [
		{
			title: 'Treatment of chronic <br /> conditions',
			description:
				'Expert care for chronic conditions including specialist consultations, laboratory tests, and chronic disease medication.',
			icon: {
				icon: 'radiation',
				dir: 'random'
			}
		},
		{
			title: 'Intensive care <br /> unit',
			description:
				'Offer your team personalized care and 24/7 support, including access to the Intensive Care Unit when the need arises.',
			icon: {
				icon: 'health-pack',
				dir: 'random'
			}
		},
		{
			title: 'Flexible gym <br/> access',
			description: "Invest in your team's well-being and watch productivity soar with our flexible gym access.",
			icon: {
				icon: 'gym-handle',
				dir: 'random'
			}
		},
		{
			title: 'Admissions and <br /> accommodation',
			description: 'Your staff enjoy quality healthcare while at admitted at any of our hospitals.',
			icon: {
				icon: 'hospital-bed',
				dir: 'random'
			}
		},
		{
			title: 'Accident <br /> and emergency care',
			description:
				'Your team gets prompt and expert care for accidents and emergencies through our comprehensive insurance plan.',
			icon: {
				icon: 'reflective-triangle',
				dir: 'random'
			}
		},
		{
			title: 'Virtual <br /> Consultations',
			description: 'Your staff can connect virtually to medical doctors.',
			icon: {
				icon: 'virtual-consultation',
				dir: 'fill'
			}
		}
	]
</script>

<template>
	<div>
		<header class="md:min-h-[770px] mt-0 bg-health-800 sm:px-5 md:px-0 pt-12 md:py-24">
			<ui-pad :ui="{ paddingBlock: '' }" class="w-full">
				<div class="flex items-center justify-between w-full lg:flex-nowrap flex-wrap gap-y-10">
					<ui-h1
						paragraph="Premium Health Insurance plans designed to <br /> give you and your staff complete peace of mind."
						:ui="btnClass"
						tag="ZenCare"
						:tag-icon="{ dir: 'fill', icon: 'heart-rate-pulse' }"
						primary-btn-text="Enroll your staff in 5 minutes or less "
						secondary-btn-text="Speak to us to get on board"
						:action="{ type: 'onboard', product: 'zencare' }"
					>
						<template #title="{ highlightColor }">
							<span>
								Premium health insurance for <span :class="highlightColor">smart businesses</span>
							</span>
						</template>
					</ui-h1>

					<ui-image-frame-2
						alt="Group of smart business professionals representing ZenCare HMO"
						src="/v1727082910/zencare-lead_bsllzz.webp"
						:hint-list="[
							{
								label: 'Cancer care coverage',
								icon: 'check-ticked',
								colorClass: 'bg-health-500',
								class: 'text-health-900',
								bgHex: '#D3EBF8'
							},
							{
								label: 'Delivery & antenatal',
								icon: 'check-ticked-2',
								colorClass: 'bg-travel-500',
								class: 'text-travel-900',
								bgHex: '#DBD5F6'
							},
							{
								label: 'Gym coverage',
								icon: 'check-ticked-3',
								colorClass: 'bg-auto-500',
								class: 'text-auto-900',
								bgHex: '#F4D7F0'
							}
						]"
					/>
				</div>
			</ui-pad>
		</header>

		<ui-spacer />
		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-partners />
			<ui-spacer />
			<main-health-care-experience
				title="Quality & affordable healthcare at <br /> your fingertips!"
				description="Our goal is to provide your team with the best possible tools and support, ensuring their health and well-being are our top priorities."
			/>
			<ui-spacer />
			<lazy-main-productivity-benefits
				title="Benefits that enhance your team's productivity"
				description="It’s more than just health insurance; it’s about providing benefits that resonate with your team's lifestyle."
				:team-benefits="teamBenefits"
				product-name="ZenCare"
				@trigger="() => [(showBenefits = true), (focusMode = true)]"
			/>
		</ui-pad>
		<ui-spacer />
		<main-health-pricing
			product-name="zencare"
			title="Premium healthcare plans for your staff"
			description="Choose a plan that best suits your staff and business needs"
			@more-benefit="showBenefits = true"
		/>
		<ui-spacer />
		<ui-pad :ui="{ paddingBlock: '' }">
			<lazy-main-most-used-hospitals :count="1" :list="mostlyUsedHospitals" />
			<ui-spacer :ui="{ paddingBlock: '', marginBlock: ' sm:mb-28' }" />
			<lazy-molecules-hospital-list group-key="zencare" class="hidden md:block" />
			<ui-spacer />
			<lazy-main-benefit-list-display
				product-category="health"
				product-name="zencare"
				title="Get comprehensive healthcare coverage for your business."
				description="Find the Right Plan to Fit Your Company's Needs"
			/>
			<ui-spacer />
			<lazy-molecules-process-steps
				:processes="onboardingProcesses"
				title="Simple. Fast. Affordable."
				description="Onboard your team in 3 easy steps!"
				:ui="{ strokeText: { strokeColor: '#A6D7F2', color: 'text-health-100' }, header: { width: '' } }"
				@trigger="() => (isDialogOpen = true)"
			>
				<template #btn>
					<ui-btn
						:ui="{
							btn: {
								base: 'bg-health-500 mt-8 min-h-[56px]',
								onHoverPseudo: 'hover:before:bg-health-800 '
							},
							text: {
								base: 'text-white'
							}
						}"
						@click="navigateToProduct('zencare', { category: 'health' })"
					>
						<span>Get Covered Now</span>
						<template #icon>
							<tools-icon dir="fill" icon="heart-rate-pulse" />
						</template>
					</ui-btn>
				</template>
			</lazy-molecules-process-steps>
		</ui-pad>
		<ui-spacer />
		<lazy-molecules-important-guides />
		<ui-spacer />
		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-testimonials />
		</ui-pad>
		<ui-spacer />
		<molecules-demo-card
			title="Comprehensive coverage for your business."
			description=""
			type="health"
			@secondary-click="viewMoreProducts('health')"
		/>
		<ui-spacer />
		<ui-pad :ui="{ paddingBlock: ' ' }">
			<molecules-investors class="pt-12 pb-20" />
			<main-faq :faq-list="faqList" />
		</ui-pad>
		<ui-spacer />

		<lazy-molecules-benefit-dialog
			v-model:show="showBenefits"
			product-name="zencare"
			product-category="health"
			v-model:focus-on="focusMode"
		/>

		<lazy-molecules-auth-dialog v-model="isDialogOpen" selected="register" />
	</div>
</template>
